import React, { useState, useEffect } from 'react';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, User, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/features/auth/AuthProvider';
import { formatCurrency } from '@/lib/utils';
import { useToast } from '@/hooks/useToast';
import { Link } from 'react-router-dom';
import { writeErrorMessage, logWriteError } from '@/lib/firestoreListenerErrors';
import { useActiveChild } from '@/contexts/ActiveChildContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import { UNDECIDED_PLAN } from '@/lib/plans';

interface Props {
    onComplete: () => void;
    targetUserIds?: string[]; // Array of user IDs (for multi-child support)
    targetUserId?: string; // Legacy single user support
}

interface UserInfo {
    id: string;
    name: string;
    selectedPlanId: string | null;
}

export const SubscriptionStep: React.FC<Props> = ({ onComplete, targetUserIds, targetUserId }) => {
    const { t } = useTranslation();
    const { plans, assignSubscription } = useSubscriptions();
    const { currentUser } = useAuth();
    const { activeChildId } = useActiveChild();
    const { success, error: showError } = useToast();
    /** Non-null when the step failed — rendered with a way out, not just a toast. */
    const [failure, setFailure] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [usersInfo, setUsersInfo] = useState<UserInfo[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(true);

    // Determine effective user IDs
    // Priority: targetUserIds array -> targetUserId single -> activeChildId -> currentUser
    const effectiveUserIds = targetUserIds && targetUserIds.length > 0
        ? targetUserIds
        : targetUserId
            ? [targetUserId]
            : activeChildId
                ? [activeChildId]
                : currentUser?.uid
                    ? [currentUser.uid]
                    : [];

    // Filter for public, active plans
    const activePlans = plans.filter(p => p.isActive && p.isPublic !== false);

    // Fetch user names on mount
    useEffect(() => {
        const fetchUserNames = async () => {
            setLoadingUsers(true);
            try {
                const userInfos: UserInfo[] = await Promise.all(
                    effectiveUserIds.map(async (userId) => {
                        const userDoc = await getDoc(doc(db, 'users', userId));
                        const userData = userDoc.exists() ? userDoc.data() : null;
                        // Start on the plan this member ASKED for at signup, when it is still
                        // offered — the family was already asked once; this step is where the
                        // subscription is actually created, so it confirms rather than re-asks.
                        const asked = userData?.requestedPlanId as string | undefined;
                        const stillOffered = asked && asked !== UNDECIDED_PLAN
                            && plans.some(p => p.id === asked && p.isActive && p.isPublic !== false);
                        return {
                            id: userId,
                            name: userData?.displayName || 'Unknown User',
                            selectedPlanId: stillOffered ? asked! : null
                        };
                    })
                );
                setUsersInfo(userInfos);
            } catch (err) {
                console.error('Error fetching user info:', err);
            } finally {
                setLoadingUsers(false);
            }
        };

        if (effectiveUserIds.length > 0) {
            fetchUserNames();
        }
    }, [effectiveUserIds.join(','), plans.length]);

    const handlePlanChange = (userId: string, planId: string) => {
        setUsersInfo(prev => prev.map(u =>
            u.id === userId ? { ...u, selectedPlanId: planId } : u
        ));
    };

    const allPlansSelected = usersInfo.every(u => u.selectedPlanId !== null);

    const calculateTotal = () => {
        return usersInfo.reduce((total, user) => {
            const plan = plans.find(p => p.id === user.selectedPlanId);
            return total + (plan?.price || 0);
        }, 0);
    };

    const handleContinue = async () => {
        if (!allPlansSelected || !currentUser) {
            showError("Please select a plan for each member");
            return;
        }

        setLoading(true);
        setFailure(null);
        try {
            // Assign subscriptions for all users
            for (const user of usersInfo) {
                const plan = plans.find(p => p.id === user.selectedPlanId);
                if (!plan) continue;

                await assignSubscription(
                    user.id,
                    currentUser.displayName || 'Unknown User', // Payer Name (Parent)
                    plan.id,
                    'manual',
                    undefined, // promoCode
                    currentUser.branchId, // branchId
                    undefined, // customStartDate
                    undefined, // assignedCoachId
                    true // skipIncomeRecord: Do NOT record income for registration
                );
            }

            success(`Plans selected for ${usersInfo.length} member${usersInfo.length > 1 ? 's' : ''}`);
            onComplete();
        } catch (err) {
            // Was: console.error(err) + "Failed to select plans. Please try again."
            // The real failure was a rules denial, so "try again" could never work —
            // the member was told to repeat a guaranteed failure and we got no
            // diagnostic. Two people were turned away before it was noticed (P0,
            // 2026-09-02). Surface what actually happened, in their language, and
            // give them a way out that is not a dead retry button.
            logWriteError(
                { operation: 'assignSubscription', collection: 'userSubscriptions+users', uid: currentUser?.uid },
                err,
            );
            const message = writeErrorMessage(t, err);
            setFailure(message);
            showError(message);
        } finally {
            setLoading(false);
        }
    };

    if (loadingUsers) {
        return <div className="flex justify-center py-12">{t('common.loading')}</div>;
    }

    // Single user mode - show plan cards (legacy UI)
    if (usersInfo.length === 1) {
        return (
            <div className="space-y-6">
                <div className="text-center space-y-2">
                    <h2 className="text-2xl font-bold">{t('onboarding.subscription.choose_plan')}</h2>
                    <p className="text-muted-foreground">{t('onboarding.subscription.select_membership', { name: usersInfo[0].name })}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {activePlans.map((plan) => {
                        const isSelected = usersInfo[0].selectedPlanId === plan.id;
                        return (
                            <Card
                                key={plan.id}
                                className={`flex flex-col relative cursor-pointer transition-all ${isSelected ? 'border-primary ring-2 ring-primary/20 shadow-lg' : 'hover:border-primary/50'} ${plan.isFeatured && !isSelected ? 'border-primary/30' : ''}`}
                                onClick={() => handlePlanChange(usersInfo[0].id, plan.id)}
                            >
                                {plan.isFeatured && (
                                    <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg z-10 shadow-sm">
                                        {t('onboarding.subscription.recommended')}
                                    </div>
                                )}
                                {isSelected && (
                                    <div className="absolute top-3 left-3 bg-primary text-primary-foreground rounded-full p-1">
                                        <Check className="h-4 w-4" />
                                    </div>
                                )}
                                <CardHeader>
                                    <CardTitle>{plan.name}</CardTitle>
                                    <CardDescription>{plan.description || t('onboarding.subscription.membership_plan')}</CardDescription>
                                </CardHeader>
                                <CardContent className="flex-1">
                                    <div className="flex flex-wrap items-baseline gap-1 mb-4">
                                        <span className={`font-bold ${plan.price > 9999 ? 'text-2xl' : 'text-3xl'}`}>
                                            {formatCurrency(plan.price)}
                                        </span>
                                        <span className="text-sm font-normal text-muted-foreground whitespace-nowrap">{t('onboarding.subscription.per_month')}</span>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* A member who cannot finish signup needs the real reason and a route
                    to a human — not a retry button that is guaranteed to fail again. */}
                {failure && (
                    <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3" role="alert">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
                            <div className="min-w-0 space-y-2">
                                <p className="text-sm text-destructive" dir="auto">{failure}</p>
                                <Link
                                    to="/contact-support"
                                    className="inline-block text-sm font-medium underline underline-offset-2"
                                >
                                    {t('errors.write.signup_failed_contact')}
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
                <div className="pt-6 flex justify-end">
                    <Button onClick={handleContinue} disabled={!allPlansSelected || loading} size="lg">
                        {loading ? t('onboarding.subscription.processing') : t('onboarding.subscription.continue')}
                    </Button>
                </div>
            </div>
        );
    }

    // Multi-user mode - show table with dropdown per user
    return (
        <div className="space-y-6">
            <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold">{t('onboarding.subscription.choose_plans')}</h2>
                <p className="text-muted-foreground">{t('onboarding.subscription.select_plan_each')}</p>
            </div>

            <div className="space-y-4">
                {usersInfo.map((user, idx) => (
                    <Card key={user.id} className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                    <User className="h-5 w-5 text-primary" />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-medium truncate">{user.name}</p>
                                    <p className="text-xs text-muted-foreground">{t('onboarding.subscription.member_number', { num: idx + 1 })}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 sm:w-80">
                                <Select
                                    value={user.selectedPlanId || ''}
                                    onValueChange={(val) => handlePlanChange(user.id, val)}
                                >
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder={t('onboarding.subscription.select_a_plan')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {activePlans.map(plan => (
                                            <SelectItem key={plan.id} value={plan.id}>
                                                {t('onboarding.subscription.plan_option', { name: plan.name, price: formatCurrency(plan.price) })}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>

                                {user.selectedPlanId && (
                                    <span className="text-sm font-semibold text-primary shrink-0">
                                        {formatCurrency(plans.find(p => p.id === user.selectedPlanId)?.price || 0)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            {/* Total */}
            {allPlansSelected && (
                <Card className="bg-primary/5 border-primary/20 p-4">
                    <div className="flex justify-between items-center">
                        <span className="font-medium">{t('onboarding.subscription.total_monthly')}</span>
                        <span className="text-2xl font-bold text-primary">{formatCurrency(calculateTotal())}</span>
                    </div>
                </Card>
            )}

            {/* A member who cannot finish signup needs the real reason and a route
                to a human — not a retry button that is guaranteed to fail again. */}
            {failure && (
                <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3" role="alert">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
                        <div className="min-w-0 space-y-2">
                        <p className="text-sm text-destructive" dir="auto">{failure}</p>
                        <Link
                            to="/contact-support"
                            className="inline-block text-sm font-medium underline underline-offset-2"
                        >
                            {t('errors.write.signup_failed_contact')}
                        </Link>
                        </div>
                    </div>
                </div>
            )}
            <div className="pt-4 flex justify-end">
                <Button onClick={handleContinue} disabled={!allPlansSelected || loading} size="lg">
                    {loading ? t('onboarding.subscription.processing') : t('onboarding.subscription.continue_with_plans', { count: usersInfo.length })}
                </Button>
            </div>

            {plans.length === 0 && (
                <div className="text-center text-muted-foreground p-8 border rounded-lg border-dashed">
                    {t('onboarding.subscription.no_plans_available')}
                    <div className="mt-4">
                        <Button variant="outline" onClick={onComplete}>{t('onboarding.classes.skip_dev')}</Button>
                    </div>
                </div>
            )}
        </div>
    );
};

